import { useState, useEffect, useRef } from 'react';
import api from '../api';

function NeuralCanvas() {
  const canvasRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);

    const NODE_COUNT = 80;
    const nodes = Array.from({ length: NODE_COUNT }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 2.5 + 1, pulse: Math.random() * Math.PI * 2,
    }));

    const PARTICLE_COUNT = 60;
    const particles = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.2, vy: (Math.random() - 0.5) * 0.2,
      r: Math.random() * 1.2 + 0.3, alpha: Math.random() * 0.5 + 0.2,
    }));

    let t = 0;
    const draw = () => {
      ctx.fillStyle = 'rgba(8, 14, 25, 0.18)';
      ctx.fillRect(0, 0, W, H);
      t += 0.008;

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 160) {
            const alpha = (1 - dist / 160) * 0.25;
            const pulse = Math.sin(t * 2 + nodes[i].pulse) * 0.5 + 0.5;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(59, 130, 246, ${alpha * pulse})`;
            ctx.lineWidth = 0.8;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      nodes.forEach(n => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
        n.pulse += 0.02;
        const glow = Math.sin(n.pulse) * 0.4 + 0.6;
        const grad = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r * 4);
        grad.addColorStop(0, `rgba(99, 179, 237, ${0.9 * glow})`);
        grad.addColorStop(1, 'rgba(59, 130, 246, 0)');
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r * 4, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(147, 210, 255, ${glow})`;
        ctx.fill();
      });

      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200, 230, 255, ${p.alpha})`;
        ctx.fill();
      });

      const cx = W / 2, cy = H / 2;
      const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 300);
      glow.addColorStop(0, `rgba(59, 130, 246, ${0.04 + Math.sin(t) * 0.02})`);
      glow.addColorStop(1, 'rgba(59, 130, 246, 0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, W, H);

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', background: '#080e19' }}/>;
}

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setTimeout(() => setMounted(true), 100); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/login', { email, password });
      localStorage.setItem('token', data.token);
      onLogin(data.user);
    } catch(err) {
      setError(err.response?.data?.error || 'Ошибка входа');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden', background: '#080e19' }}>
      <NeuralCanvas/>

      <div style={{
        position: 'relative', zIndex: 10,
        minHeight: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 40,
      }}>
        {/* Логотип и слоган */}
        <div style={{
          textAlign: 'center',
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(-20px)',
          transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          <div style={{
            fontSize: 56, fontWeight: 800, letterSpacing: '-3px', lineHeight: 1, marginBottom: 12,
          }}>
            <span style={{ color: '#e2e8f0' }}>by</span>
            <span style={{
              color: 'transparent',
              backgroundImage: 'linear-gradient(135deg, #3b82f6, #60a5fa, #93c5fd)',
              WebkitBackgroundClip: 'text', backgroundClip: 'text',
            }}>stats</span>
          </div>
          <div style={{
            fontSize: 13, color: 'rgba(148, 163, 184, 0.7)',
            letterSpacing: '4px', textTransform: 'uppercase', fontWeight: 500,
          }}>
            следи · управляй · расти
          </div>
        </div>

        {/* Форма */}
        <div style={{
          width: 360,
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.15s',
        }}>
          <div style={{
            background: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(24px)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: 20, padding: '36px 32px',
            boxShadow: '0 0 0 1px rgba(59,130,246,0.05), 0 32px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
          }}>
            <div style={{ marginBottom: 28, textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#e2e8f0', marginBottom: 4 }}>Добро пожаловать</div>
              <div style={{ fontSize: 13, color: 'rgba(148,163,184,0.5)' }}>Войдите в свой аккаунт</div>
            </div>

            <form onSubmit={submit}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)', marginBottom: 6, letterSpacing: '0.8px', textTransform: 'uppercase', fontWeight: 600 }}>Логин</div>
                <input type="text" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="Введите логин" required
                  style={{
                    width: '100%', background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(59,130,246,0.2)', borderRadius: 10,
                    color: '#e2e8f0', fontSize: 14, padding: '11px 14px',
                    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
                  }}
                  onFocus={e => e.target.style.borderColor = 'rgba(59,130,246,0.6)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(59,130,246,0.2)'}
                />
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)', marginBottom: 6, letterSpacing: '0.8px', textTransform: 'uppercase', fontWeight: 600 }}>Пароль</div>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" required
                  style={{
                    width: '100%', background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(59,130,246,0.2)', borderRadius: 10,
                    color: '#e2e8f0', fontSize: 14, padding: '11px 14px',
                    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
                  }}
                  onFocus={e => e.target.style.borderColor = 'rgba(59,130,246,0.6)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(59,130,246,0.2)'}
                />
              </div>
              {error && (
                <div style={{
                  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                  borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                  color: '#fca5a5', fontSize: 13,
                }}>{error}</div>
              )}
              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '13px',
                background: loading ? 'rgba(59,130,246,0.3)' : 'linear-gradient(135deg, #1d4ed8, #3b82f6)',
                color: '#fff', fontSize: 14, fontWeight: 600, borderRadius: 10,
                border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 0 24px rgba(59,130,246,0.25)',
                transition: 'all 0.2s', letterSpacing: '0.3px',
              }}>
                {loading ? '⟳ Вход...' : 'Войти →'}
              </button>
            </form>
          </div>
        </div>

        {/* Футер */}
        <div style={{
          fontSize: 11, color: 'rgba(100,116,139,0.4)', letterSpacing: '2px',
          opacity: mounted ? 1 : 0, transition: 'opacity 0.8s 0.4s',
        }}>
          WILDBERRIES ANALYTICS PLATFORM
        </div>
      </div>
    </div>
  );
}
